import React, { Dispatch, SetStateAction, useContext } from 'react';
import ButtonPrimary from '@components/common/ButtonPrimary';
import ButtonSecondary from '@components/common/ButtonSecondary';
import Modal from '@components/common/Modal/Modal';
import actionTypes from '@context/actionTypes';
import { store } from '@context/store';
import Image from 'next/image';
import calendarIcon from '@assets/images/calendar-rounded-icon.svg';
import useCheckout from '@hooks/useCheckout';
import { CartItemDetails } from '@customTypes/cart-items';
import { CheckoutInfo } from '@customTypes/checkout-info';
import Button from '@solar/common/Button/ButtonV2';
import {
  AMPLITUDE_EXPERIMENT_CONTROL_NAME,
  AMPLITUDE_EXPERIMENT_VARIANT_NAME,
} from '@constants/amplitudeExperiments';
import { useFeatureFlagContext } from '@context/FeatureFlagContext';

type Props = {
  checkoutDetails: CheckoutInfo | undefined;
  setCartData: Dispatch<SetStateAction<CartItemDetails | undefined>>;
};

export default function SameDayBookingWarning({ checkoutDetails, setCartData }: Props) {
  const { dispatch, state } = useContext(store);
  const { navigateToCheckoutConfirmation } = useCheckout();
  const { ctasUpdateExperiment } = useFeatureFlagContext();

  const handleClosePopup = () => {
    dispatch({
      type: actionTypes.OPEN_SAME_DAY_BOOKING_WARNING,
      payload: false,
    });
  };

  const renderOriginalButtons = () => (
    <div className="flex flex-col justify-center mt-9 space-y-3 w-full d:flex-row-reverse d:space-x-4 d:space-x-reverse d:space-y-0">
      <ButtonPrimary
        classes="d:max-w-200"
        onClick={() => {
          navigateToCheckoutConfirmation(dispatch, setCartData, checkoutDetails);
          handleClosePopup();
        }}
      >
        Continue to Checkout
      </ButtonPrimary>

      <ButtonSecondary
        classes="d:max-w-200"
        onClick={() => {
          dispatch({
            type: actionTypes.OPEN_SAME_DAY_BOOKING_WARNING,
            payload: false,
          });

          dispatch({
            type: actionTypes.OPEN_DATE_PICKER,
            payload: true,
          });
        }}
      >
        Change Date
      </ButtonSecondary>
    </div>
  );

  const renderUpdatedCTAButtons = () => (
    <div className="flex flex-col-reverse justify-center space-y-reverse space-y-spacing-md mt-6 w-full d:flex-row d:space-x-4 d:space-y-0 d:flex-row">
      <div className="d:w-50">
        <Button
          size="lg"
          variant="secondary"
          onClick={() => {
            dispatch({
              type: actionTypes.OPEN_SAME_DAY_BOOKING_WARNING,
              payload: false,
            });

            dispatch({
              type: actionTypes.OPEN_DATE_PICKER,
              payload: true,
            });
          }}
        >
          Change Date
        </Button>
      </div>

      <div className="d:w-50">
        <Button
          size="lg"
          variant={
            ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_VARIANT_NAME ? 'primary' : 'ultramarine'
          }
          onClick={() => {
            navigateToCheckoutConfirmation(dispatch, setCartData, checkoutDetails);
            handleClosePopup();
          }}
        >
          Continue To Checkout
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      closePopup={handleClosePopup}
      containerClasses="px-spacing-md py-10 d:px-2"
      hideScrollBar
      isOpen={state.showSameDayBookingWarning}
      showCloseButton={false}
    >
      <div className="flex flex-col items-center w-full">
        <div className="h-20 relative w-20">
          <Image src={calendarIcon} layout="fill" alt="Same day booking warning icon" />
        </div>

        <p className="-tracking-0.5 font-rp-pn-regular font-semibold leading-35px mt-9 text-28 text-center text-rp-primary-black d:mt-5 d:max-w-493">
          Heads up!
          <br /> You&apos;re about to book a daycation for today.
        </p>

        <p className="font-rp-pn-regular leading-25.5px mt-9 opacity-70 text-17 text-center text-rp-primary-black d:max-w-493 d:mt-4">
          Today is a perfect day for a daycation! We simply want to verify that you&apos;re aware
          the booking is for today.
        </p>

        {!ctasUpdateExperiment || ctasUpdateExperiment === AMPLITUDE_EXPERIMENT_CONTROL_NAME
          ? renderOriginalButtons()
          : renderUpdatedCTAButtons()}
      </div>
    </Modal>
  );
}
